import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { convertFile, fetchConvertCapabilities, triggerDownload } from '../../services/officeToolsApi.js';
import { rememberRecentApp } from '../../utils/recentApps.js';
import {
  FiAlertCircle,
  FiAlertTriangle,
  FiArrowLeft,
  FiCheck,
  FiCheckCircle,
  FiDownload,
  FiFileText,
  FiLock,
  FiRepeat
} from 'react-icons/fi';
import ThemeToggle from '../ThemeToggle.jsx';
import TourButton from '../tour/TourButton.jsx';
import HelpMenu, { FeedbackButton } from '../help/HelpMenu.jsx';
import TourMode from '../tour/TourMode.jsx';
import { usePageTour } from '../tour/usePageTour.js';
import FileDrop from './FileDrop.jsx';
import '../../styles/office-tools.css';

const CONVERT_TOUR_STEPS = [
  {
    target: 'convert-caps',
    title: 'What can be converted',
    body: 'A quick health check of the converter. Green ticks are ready to use; an amber warning means Office formats need LibreOffice on the server.'
  },
  {
    target: 'convert-drop',
    title: 'Add your file',
    body: 'Click to browse or simply drag a file onto this box. We detect whether it is an image, text, or Office document.'
  },
  {
    target: 'convert-format',
    title: 'Pick the output',
    body: 'Only formats that make sense for your file are shown. Tap one to select it.'
  },
  {
    target: 'convert-action',
    title: 'Convert & download',
    body: 'The converted file downloads straight away. Nothing is kept on the server afterwards.'
  }
];

const DEFAULT_FORMATS = [
  { id: 'pdf', label: 'PDF', ext: 'pdf' },
  { id: 'docx', label: 'Word (DOCX)', ext: 'docx' },
  { id: 'odt', label: 'OpenDocument Text (ODT)', ext: 'odt' },
  { id: 'txt', label: 'Plain text (TXT)', ext: 'txt' },
  { id: 'html', label: 'HTML', ext: 'html' },
  { id: 'rtf', label: 'RTF', ext: 'rtf' },
  { id: 'xlsx', label: 'Excel (XLSX)', ext: 'xlsx' },
  { id: 'csv', label: 'CSV', ext: 'csv' },
  { id: 'pptx', label: 'PowerPoint (PPTX)', ext: 'pptx' },
  { id: 'png', label: 'PNG image', ext: 'png' },
  { id: 'jpg', label: 'JPEG image', ext: 'jpg' },
  { id: 'webp', label: 'WebP image', ext: 'webp' }
];

function guessKind(file) {
  if (!file) return 'unknown';
  const name = file.name.toLowerCase();
  const type = (file.type || '').toLowerCase();
  if (type.startsWith('image/') || /\.(png|jpe?g|webp|gif|bmp|tiff?)$/.test(name)) return 'image';
  if (type.startsWith('text/') || /\.(txt|md|csv|log|json|xml|html?)$/.test(name)) return 'text';
  if (/\.(pdf|docx?|odt|rtf|pptx?|xlsx?|ods|odp)$/.test(name) || type.includes('officedocument') || type === 'application/pdf') {
    return 'office';
  }
  return 'unknown';
}

// Targets the backend converts natively; everything else goes through LibreOffice
// (see backend/services/fileConvertService.js), so only offer it when LibreOffice is available.
const NATIVE_TARGETS = { image: ['pdf', 'png', 'jpg', 'webp'], text: ['pdf', 'txt', 'html'] };

function formatsForKind(kind, allFormats, officeAvailable) {
  const list = allFormats.length ? allFormats : DEFAULT_FORMATS;
  if (kind === 'image') {
    const ids = officeAvailable ? [...NATIVE_TARGETS.image, 'docx', 'odt', 'txt'] : NATIVE_TARGETS.image;
    return list.filter((f) => ids.includes(f.id));
  }
  if (kind === 'text') {
    const ids = officeAvailable ? [...NATIVE_TARGETS.text, 'docx', 'odt', 'rtf'] : NATIVE_TARGETS.text;
    return list.filter((f) => ids.includes(f.id));
  }
  if (kind === 'office') {
    const officeTargets = ['pdf', 'docx', 'odt', 'txt', 'html', 'rtf', 'xlsx', 'csv', 'pptx'];
    return list.filter((f) => officeTargets.includes(f.id));
  }
  return list.filter((f) => f.id === 'pdf' || officeAvailable);
}

export default function FileConvertPage() {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [targetFormat, setTargetFormat] = useState('pdf');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const { touring, start: startTour, end: endTour } = usePageTour();
  const [caps, setCaps] = useState({ images: true, text: true, office: false, formats: DEFAULT_FORMATS });

  useEffect(() => {
    fetchConvertCapabilities()
      .then((data) => setCaps({
        images: true,
        text: true,
        office: Boolean(data.office || data.libreOffice),
        formats: Array.isArray(data.formats) && data.formats.length ? data.formats : DEFAULT_FORMATS,
        maxFileMb: data.maxFileMb || 20
      }))
      .catch(() => {});
  }, []);

  const kind = guessKind(file);
  const availableFormats = useMemo(
    () => formatsForKind(kind, caps.formats || DEFAULT_FORMATS, caps.office),
    [kind, caps.formats, caps.office]
  );

  useEffect(() => {
    if (!availableFormats.some((f) => f.id === targetFormat)) {
      setTargetFormat(availableFormats[0]?.id || 'pdf');
    }
  }, [availableFormats, targetFormat]);

  const onConvert = async () => {
    if (!file) {
      setError('Choose a file first');
      return;
    }
    if (kind === 'office' && !caps.office) {
      setError('LibreOffice is not available on the server yet. Redeploy the backend image, or convert an image/text file instead.');
      return;
    }
    setError('');
    setStatus('');
    setBusy(true);
    try {
      const result = await convertFile(file, targetFormat);
      triggerDownload(result);
      rememberRecentApp('file-convert');
      const method = result.headers.get('X-NovaBotX-Convert-Method') || '';
      setStatus(`Converted to ${result.filename}${method ? ` · ${method}` : ''}`);
    } catch (err) {
      setError(err.message || 'Could not convert file');
    } finally {
      setBusy(false);
    }
  };

  const kindLabel = { image: 'Image', text: 'Text', office: 'Office / PDF', unknown: 'Other' }[kind] || kind;

  return (
    <div className="office-tools-page">
      <header className="office-tools-topbar">
        <button type="button" className="office-tools-back" onClick={() => navigate('/apps')}>
          <FiArrowLeft aria-hidden="true" /> <span>Back to My Apps</span>
        </button>
        <div className="office-tools-topbar-actions">
          <HelpMenu onTour={startTour} />
          <FeedbackButton />
          <TourButton onClick={startTour} />
          <ThemeToggle compact />
        </div>
      </header>

      <div className="office-tools-shell">
        <header className="office-tools-header">
          <span className="office-tools-hero-icon" aria-hidden="true"><FiRepeat /></span>
          <div>
            <span className="office-tools-kicker">Office tools</span>
            <h1>File Convert</h1>
            <p>
              Convert between images, text, PDF, and Office formats. Pick any supported input, then choose the output type.
            </p>
            <span className="office-tools-privacy"><FiLock aria-hidden="true" /> Files are not stored after download</span>
          </div>
        </header>

        {error && (
          <div className="office-tools-alert is-error" role="alert">
            <FiAlertCircle aria-hidden="true" /> <span>{error}</span>
          </div>
        )}
        {status && !error && (
          <div className="office-tools-alert is-ok">
            <FiCheckCircle aria-hidden="true" /> <span>{status}</span>
          </div>
        )}

        <section className={`office-tools-card ${busy ? 'is-busy' : ''}`}>
          <h2>Convert file</h2>
          <ul className="office-tools-caps" data-tour="convert-caps">
            <li className={caps.images ? 'is-on' : ''}>
              {caps.images ? <FiCheck aria-hidden="true" /> : <FiAlertTriangle aria-hidden="true" />}
              Images ↔ PDF / PNG / JPG / WebP
            </li>
            <li className={caps.text ? 'is-on' : ''}>
              {caps.text ? <FiCheck aria-hidden="true" /> : <FiAlertTriangle aria-hidden="true" />}
              Text ↔ PDF / TXT / HTML{caps.office ? ' / DOCX' : ''}
            </li>
            <li className={caps.office ? 'is-on' : 'is-off'}>
              {caps.office ? <FiCheck aria-hidden="true" /> : <FiAlertTriangle aria-hidden="true" />}
              Office / PDF{caps.office ? ' (LibreOffice ready)' : ' (LibreOffice missing — redeploy backend)'}
            </li>
          </ul>

          <FileDrop
            className="office-tools-file"
            data-tour="convert-drop"
            title={file ? 'Choose a different file' : 'Choose file'}
            hint="or drag and drop it here"
            onFiles={(files) => {
              setFile(files[0] || null);
              setError('');
              setStatus('');
            }}
          />
          {file && (
            <div className="office-tools-file-chip" key={`${file.name}-${file.size}`}>
              <FiFileText aria-hidden="true" />
              <p className="office-tools-filename">{file.name}</p>
              <span className="office-tools-kind">{kindLabel}</span>
            </div>
          )}

          <div className="office-tools-select-label" data-tour="convert-format">
            <span id="convert-to-label">Convert to</span>
            <div className="office-tools-formats" role="radiogroup" aria-labelledby="convert-to-label">
              {availableFormats.map((fmt) => (
                <button
                  key={fmt.id}
                  type="button"
                  role="radio"
                  aria-checked={targetFormat === fmt.id}
                  className={`office-tools-format ${targetFormat === fmt.id ? 'is-active' : ''}`}
                  onClick={() => setTargetFormat(fmt.id)}
                  title={fmt.label || fmt.id.toUpperCase()}
                >
                  <strong>{(fmt.ext || fmt.id).toUpperCase()}</strong>
                  <span>{fmt.label || fmt.id.toUpperCase()}</span>
                </button>
              ))}
            </div>
          </div>

          <button
            type="button"
            className="office-tools-primary"
            disabled={busy || !file}
            onClick={onConvert}
            data-tour="convert-action"
          >
            {busy ? <span className="office-tools-spinner" aria-hidden="true" /> : <FiDownload aria-hidden="true" />}
            {busy ? 'Converting…' : `Convert & download .${targetFormat}`}
          </button>
          {busy && <div className="office-tools-progress" aria-hidden="true" />}
        </section>
      </div>
      {touring && <TourMode steps={CONVERT_TOUR_STEPS} topOffset={64} onClose={endTour} />}
    </div>
  );
}
